// Справка „По сервитьори" (Справки → waiters).
//
// Кой колко е направил в периода и как е събрано. `GET
// /reports/analytics/waiters` връща целия период наведнъж (`summary` по
// сервитьор + `data` по сервитьор и ден), затова таблицата работи в
// КЛИЕНТСКИ режим, а кликът на ред отваря дните без нова заявка.
//
// Методите на плащане са КАТО в „Продажби по периоди": всички активни (+
// неактивните с пари в периода), в реда от настройките, със смесеното
// разпределено в „В брой" и „С карта" (виж `waiters/waitersMath.ts`). Една
// лента под кутийките ги показва за целия период, а таблицата и детайлът по
// дни — по колона на метод.
//
// „Отстъпки" и „Сторно" не са в отговора на тази справка — сглобяват се от
// съседните две справки и се появяват САМО при правата за тях.

import { useCallback, useMemo, useState } from "react";
import { Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useWaitersReport } from "../../../hooks/useReports";
import {
  usePaymentMethods,
  useActivePaymentMethods,
} from "../../../hooks/usePaymentMethods";
import {
  buildMethodEntries,
  type MethodEntry,
} from "../../../utils/paymentMethodsDisplay";
import { useAppSelector } from "../../../store/hooks";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable, parseDecimal } from "../../../components/dataTable";
import { getDiscountsReport } from "../../../api/reportsDiscounts";
import { getCancellations } from "../../../api/reportsCancellations";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { pluralLabel } from "./reportPlural";
import { StationsMethodSplit } from "./stations/StationsMethodSplit";
import { WaitersKpis } from "./waiters/WaitersKpis";
import { WaiterDaysModal } from "./waiters/WaiterDaysModal";
import { WaiterMobileCard } from "./waiters/WaiterMobileCard";
import { useWaitersColumns } from "./waiters/waitersColumns";
import { exportWaiters } from "./waiters/waitersExport";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";
import {
  MIXED_PAYMENT_METHOD_ID,
  buildWaiterDayRows,
  buildWaiterIdByName,
  buildWaiterRows,
  methodTotalsOfSummary,
  summarizeWaiters,
  type WaiterRow,
} from "./waiters/waitersMath";

/** Правата на съседните справки, от които идват двете допълнителни колони. */
const DISCOUNTS_PERMISSION = "view_discounts_report";
const VOIDS_PERMISSION = "view_voids";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

/** Кой сервитьор е отворен в детайла по дни. */
type SelectedWaiter = { name: string; waiterId: number | null };

export const WaitersReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const isBg = !i18n.language?.startsWith("en");

  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState<SelectedWaiter | null>(null);
  // Редовете СЛЕД филтър и подредба — Excel изнася видяното, не суровия набор.
  const [visibleRows, setVisibleRows] = useState<WaiterRow[]>([]);

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: businessDayStartFrom(dateRange[0]).format("YYYY-MM-DD HH:mm:ss"),
      end_date: businessDayEndFrom(dateRange[1]).format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  // Предходният равен по дължина период — само за Δ чиповете. Тече ли още
  // избраният период, краят се подрязва до изтеклото (виж `reportPeriod`),
  // иначе непълен ден стои срещу пълен.
  const previousFilters = useMemo(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    const previous = previousPeriodOf(
      businessDayStartFrom(dateRange[0]),
      businessDayEndFrom(dateRange[1]),
    );
    return {
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange]);

  const { data: report, isLoading, isFetching, refetch } =
    useWaitersReport(filters);
  // При изключено сравнение се пита за същия период — React Query дава
  // резултата от кеша, без втора мрежова заявка.
  const { data: previousReport } = useWaitersReport(previousFilters ?? filters);

  const summaries = useMemo(() => report?.summary ?? [], [report]);
  const dayItems = useMemo(() => report?.data ?? [], [report]);

  /* ── допълнителните две колони: отстъпки и сторно ─────────────────────── */

  const permissions = useAppSelector((state) => state.auth.permissions);
  const canSeeDiscounts = useMemo(
    () => permissions.some((permission) => permission.name === DISCOUNTS_PERMISSION),
    [permissions],
  );
  const canSeeVoids = useMemo(
    () => permissions.some((permission) => permission.name === VOIDS_PERMISSION),
    [permissions],
  );

  // Ключовете са СЪЩИТЕ като на съседните справки — отваряш ли „Отстъпки" за
  // същия период, отговорът вече е в кеша и втора заявка не тръгва.
  const discountsQuery = useQuery({
    queryKey: ["reports", "discounts", filters],
    queryFn: () => getDiscountsReport(filters),
    staleTime: 0,
    enabled: canSeeDiscounts,
  });
  const voidsQuery = useQuery({
    queryKey: ["reports", "cancellations", filters],
    queryFn: () => getCancellations(filters),
    staleTime: 0,
    enabled: canSeeVoids,
  });
  // Отказала заявка НЕ бива да остави колона от нули: „никой не е дал
  // отстъпка" и „не успяхме да питаем" изглеждат еднакво и второто лъже.
  const showDiscounts = canSeeDiscounts && !discountsQuery.isError;
  const showVoids = canSeeVoids && !voidsQuery.isError;

  // Отстъпките и надбавките се държат РАЗДЕЛНО: `total_discount` е
  // `оригинал − нова сума`, тоест при надбавка е ОТРИЦАТЕЛНО. Слети в едно
  // число, колоната „Отстъпки" показва нето, не дадените отстъпки, а при
  // повече надбавки става отрицателна и се рисува с двоен минус („−−4.80 €").
  const adjustmentsByWaiter = useMemo(() => {
    const discounts = new Map<number, number>();
    const surcharges = new Map<number, number>();
    // Една сметка може да дойде с ПОВЕЧЕ ОТ ЕДИН ред (по един на приложена
    // отстъпка), а `total_discount` е снимка на ЦЯЛАТА сметка — броенето по
    // ред би удвоило сумата. Затова всяка сметка влиза само веднъж.
    const countedBills = new Set<number>();
    for (const row of discountsQuery.data ?? []) {
      // Сервитьорът НА СМЕТКАТА, не този, който е натиснал отстъпката —
      // иначе отстъпката, одобрена от управител на чужда маса, увисва на
      // неговия ред, а оборотът на масата стои на друг. `waiter_id` в тази
      // справка е ПРИЛАГАЩИЯТ (BE: DiscountApplied.waiter_id).
      const waiterId = row.bill_waiter_id ?? null;
      if (waiterId === null || countedBills.has(row.bill_id)) continue;
      countedBills.add(row.bill_id);
      const amount = parseDecimal(row.total_discount);
      if (amount >= 0) {
        discounts.set(waiterId, (discounts.get(waiterId) ?? 0) + amount);
      } else {
        surcharges.set(waiterId, (surcharges.get(waiterId) ?? 0) - amount);
      }
    }
    return { discounts, surcharges };
  }, [discountsQuery.data]);

  const discountsByWaiter = adjustmentsByWaiter.discounts;
  const surchargesByWaiter = adjustmentsByWaiter.surcharges;

  const voidsByWaiter = useMemo(() => {
    const byWaiter = new Map<number, number>();
    for (const row of voidsQuery.data ?? []) {
      // Сервитьорът НА СМЕТКАТА, не този, който е натиснал сторното — иначе
      // чуждото сторно увисва на управителя.
      const waiterId = row.waiter_id ?? null;
      if (waiterId === null) continue;
      byWaiter.set(
        waiterId,
        (byWaiter.get(waiterId) ?? 0) + parseDecimal(row.total_sum),
      );
    }
    return byWaiter;
  }, [voidsQuery.data]);

  /* ── редовият модел ───────────────────────────────────────────────────── */

  const waiterIdByName = useMemo(
    () => buildWaiterIdByName(dayItems),
    [dayItems],
  );

  const rows = useMemo(
    () =>
      buildWaiterRows({
        summaries,
        waiterIdByName,
        discountsByWaiter,
        surchargesByWaiter,
        voidsByWaiter,
      }),
    [
      summaries,
      waiterIdByName,
      discountsByWaiter,
      surchargesByWaiter,
      voidsByWaiter,
    ],
  );

  // Търсачката гледа само името — редът няма друг текст, по който да се търси.
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => row.waiterName.toLowerCase().includes(needle));
  }, [rows, searchText]);

  // Кутийките показват ЦЕЛИЯ период, не търсенето: числото „оборот" не бива
  // да се мени, докато човек търси конкретен сервитьор в списъка.
  const summary = useMemo(() => summarizeWaiters(rows), [rows]);
  const previousSummary = useMemo(() => {
    if (!previousFilters) return null;
    return summarizeWaiters(
      buildWaiterRows({
        summaries: previousReport?.summary ?? [],
        waiterIdByName: new Map(),
        discountsByWaiter: new Map(),
        surchargesByWaiter: new Map(),
        voidsByWaiter: new Map(),
      }),
    );
  }, [previousFilters, previousReport]);

  const { data: activeMethods = [], isLoading: activeMethodsLoading } =
    useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  // Смесеното няма своя колона/кутийка: разпределено е в „В брой" и
  // „С карта", а активният метод „Смесено" иначе би дал празна колона.
  const methodEntries = useMemo<MethodEntry[]>(
    () =>
      buildMethodEntries({
        activeMethods: activeMethods.filter(
          (method) => method.id !== MIXED_PAYMENT_METHOD_ID,
        ),
        allMethods,
        totals: methodTotalsOfSummary(summary),
        total: summary.total,
        isBg,
      }),
    [activeMethods, allMethods, summary, isBg],
  );

  const columns = useWaitersColumns(methodEntries, {
    showDiscounts,
    showVoids,
  });

  /* ── детайлът по дни ──────────────────────────────────────────────────── */

  // Денят от седмицата зависи от езика, затова се разрешава ТУК и влиза в
  // самия ред: стойност, извадена в `accessorFn`, се заковава при първия
  // рендер и смяната на език не стига до клетката.
  const formatDay = useCallback(
    (date: string) => {
      const parsed = dayjs(date);
      return {
        // Ръчен формат — `toLocaleDateString("bg-BG")` лепи „ г." и редът се
        // пренася на два реда в клетката.
        dateLabel: parsed.format("DD.MM.YYYY"),
        weekday: parsed
          .toDate()
          .toLocaleDateString(i18n.language, { weekday: "long" }),
      };
    },
    [i18n.language],
  );

  const selectedDays = useMemo(() => {
    if (!selected) return [];
    const mine = dayItems.filter((item) =>
      selected.waiterId === null
        ? item.waiterName === selected.name
        : item.waiterId === selected.waiterId,
    );
    return buildWaiterDayRows(mine, formatDay);
  }, [selected, dayItems, formatDay]);

  /* ── рендер ───────────────────────────────────────────────────────────── */

  const handleFilteredRows = useCallback(
    (next: WaiterRow[]) => setVisibleRows(next),
    [],
  );

  const handleExport = () =>
    exportWaiters({
      rows: visibleRows,
      methodEntries,
      showDiscounts,
      showVoids,
      t,
    });

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.waitersKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.waitersResultOne",
          "reportsPage.waitersResults",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.waitersRowHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.waitersTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          {/* Таблицата чака и методите: колоните им трябва да са налице при
              първия рендер, иначе запомнената им видимост се губи. */}
          {isLoading || activeMethodsLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <WaitersKpis
                summary={summary}
                previous={previousSummary}
              />

              <StationsMethodSplit
                entries={methodEntries}
                total={summary.total}
              />

              <DataTable<WaiterRow>
                tableId="waiters"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => row.rowKey}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.waitersTitle")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.waitersSearchPlaceholder"),
                }}
                onFilteredRowsChange={handleFilteredRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={handleExport}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                onRowClick={(row) =>
                  setSelected({ name: row.waiterName, waiterId: row.waiterId })
                }
                initialSorting={[{ id: "total", desc: true }]}
                mobileCard={(row) => (
                  <WaiterMobileCard
                    row={row}
                    methodEntries={methodEntries}
                    showDiscounts={showDiscounts}
                    showVoids={showVoids}
                  />
                )}
                emptyTitle={t("reportsPage.waitersEmptyTitle")}
                emptyHint={t("reportsPage.waitersEmptyHint")}
              />
            </>
          )}
        </div>
      </div>

      {selected ? (
        <WaiterDaysModal
          open
          waiterName={selected.name}
          days={selectedDays}
          methodEntries={methodEntries}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
};
